/**
 * EMPTY SHADE -- a light or dark route with no decoration while its sibling
 * has one (Jake, 2026-10-21, option 1).
 *
 * "Split light / dark" leaves an unchecked shade as an empty card. Orders SKIP
 * it: garments of that shade are produced with NO decoration on that
 * placement. The backend rule is ``is_empty_shade`` in
 * api/src/services/variant_resolver.py, which the order payload, the order
 * audit and the storefront logo picker all use; this is the editor's copy of
 * the SAME rule, so the warning the user sees matches what an order sends.
 * Keep the two identical:
 *
 *   * only ``catch_all_light`` / ``catch_all_dark`` rows can be empty;
 *   * the row has no ``artwork_file_id``;
 *   * a SIBLING row on the same placement has one. Rows with no artwork
 *     anywhere are legacy data, not an empty shade, and still apply.
 */

const SHADE_OF_KIND = Object.freeze({
    catch_all_light: "light",
    catch_all_dark: "dark",
});

export function isEmptyShade(variant, siblings) {
    if (!variant || !(variant.kind in SHADE_OF_KIND)) return false;
    if (variant.artwork_file_id) return false;
    const rows = Array.isArray(siblings) ? siblings : [];
    return rows.some((v) => v && v !== variant && v.artwork_file_id);
}

/**
 * Every empty shade on a product, in placement order.
 *
 * @param {Array} decorations  the editor's placements (each with `variants`)
 * @returns {Array<{placementId, variantId, shade: 'light'|'dark', placement}>}
 */
export function emptyShadesOf(decorations) {
    const out = [];
    for (const placement of Array.isArray(decorations) ? decorations : []) {
        const rows = Array.isArray(placement?.variants) ? placement.variants : [];
        for (const v of rows) {
            if (!isEmptyShade(v, rows)) continue;
            out.push({
                placementId: placement.id ?? placement._tempId,
                variantId: v.id ?? v._tempId,
                shade: SHADE_OF_KIND[v.kind],
                placement,
            });
        }
    }
    return out;
}
